import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Popover, PopoverContent } from '@rocco/components/popover';
import type { getPlaceDropdownOptions } from '~/modules/location/util/get-place-dropdown-options';

type PostcodeOptions = ReturnType<typeof getPlaceDropdownOptions>;

type PostcodeDropdownProps = React.ComponentProps<typeof Popover> & {
    value?: { header: string; value: { addressID?: string } };
    placeOptions?: PostcodeOptions;
    isLoading?: boolean;
    /** Index of the keyboard-highlighted option (-1 for none). */
    highlightedIndex?: number;
    onSelect?: (place: PostcodeOptions[number]) => void;
    /**
     * When false, render inside the parent instead of a body portal. Use in
     * scrollable drawers so suburb/postcode picks do not dismiss the drawer.
     */
    usePortal?: boolean;
};

/**
 * Popover dropdown for postcodes/places.
 */
export const PostcodePopover = ({
    children,
    value,
    placeOptions = [],
    isLoading = false,
    highlightedIndex = -1,
    onSelect,
    usePortal = true,
    ...props
}: PostcodeDropdownProps) => {
    const highlightedRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        highlightedRef.current?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex]);

    return (
        <Popover {...props}>
            {children}
            <PopoverContent
                align="start"
                background="transparent"
                className="min-w-sm overflow-auto rounded-lg p-0 before:rounded-lg"
                onOpenAutoFocus={(e) => e.preventDefault()}
                usePortal={usePortal}
            >
                {isLoading ? (
                    <div className="p-4" role="status" aria-live="polite">
                        Loading...
                    </div>
                ) : placeOptions.length ? (
                    <div className="flex flex-col" role="listbox">
                        {placeOptions.map((place, index) => {
                            const isHighlighted = index === highlightedIndex;
                            const isSelected =
                                place.value.addressID ===
                                value?.value.addressID;
                            return (
                                <button
                                    key={place.value.addressID}
                                    ref={
                                        isHighlighted
                                            ? highlightedRef
                                            : undefined
                                    }
                                    role="option"
                                    aria-selected={isSelected}
                                    className={cn(
                                        'align-center flex justify-between p-4 hover:bg-black/10',
                                        isHighlighted && 'bg-black/10',
                                    )}
                                    onClick={() => onSelect?.(place)}
                                >
                                    {place.header}
                                    <Check
                                        className={cn(
                                            'invisible ml-2 size-4',
                                            isSelected && 'visible',
                                        )}
                                    />
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="p-4">No results found.</div>
                )}
            </PopoverContent>
        </Popover>
    );
};
